/**
 * @typedef {import('@converse/headless/plugins/muc/types').MUCMessageAttributes} MUCMessageAttributes
 * @typedef {module:headless-plugins-muc-muc.MUCMessageData} MUCMessageData
 * @typedef {module:headless-plugins-chat-utils.MessageData} MessageData
 * @typedef {import('@converse/headless').RosterContact} RosterContact
 */
import Favico from 'favico.js-slevomat';
import { __, i18n } from 'i18n';
import { _converse, api, converse, log } from '@converse/headless';

const { Strophe, u } = converse.env;
const { isEmptyMessage, isTestEnv } = u;
const supports_html5_notification = 'Notification' in window;

converse.env.Favico = Favico;

let favicon;

// document.hidden/visibilitychange don't reliably reflect Electron's
// BrowserWindow.hide()/minimize() — confirmed live: a message arriving while
// the window was hidden to the tray still had document.hidden === false, so
// notifications never fired while "in the background" the way this app's
// close-to-tray behavior actually backgrounds it. electron/main.js pushes
// the real window state instead; this stays false (a no-op) on the web,
// where document.hidden is accurate.
let is_electron_window_hidden = false;
window.electronApp?.onWindowVisibilityChange((hidden) => {
    is_electron_window_hidden = hidden;
    console.log(`[Sonata] Electron window visibility changed: hidden=${hidden}`);
});

/**
 * @param {string} from - The JID of the sender
 * @returns {boolean}
 */
export function isMessageToHiddenChat(from) {
    // Chatboxes are keyed by bare JID, but `from` here is always the full
    // sender JID (with resource) -- looking it up unstripped never matched
    // anything, so `isHidden()` was silently never consulted and only
    // document-level visibility (tab focus) governed notifications. That
    // meant no chat's "is this the one currently open" state mattered at
    // all: while the tab had focus, nothing ever notified, for any chat.
    const jid = Strophe.getBareJidFromJid(from);
    return isTestEnv() ||
           (_converse.state.chatboxes.get(jid)?.isHidden() ?? false) ||
           document.hidden ||
           is_electron_window_hidden;
}

export function areDesktopNotificationsEnabled() {
    const enabled =
        isTestEnv() ||
        (supports_html5_notification &&
            api.settings.get('show_desktop_notifications') &&
            Notification.permission === 'granted');
    if (!enabled && !isTestEnv()) {
        // Diagnostic for the "no Windows toast ever appears" report: pins
        // down whether it's a permission problem before the notification
        // code even runs, vs. something failing after (e.g. Windows/Electron
        // silently dropping a toast it was asked to show).
        console.warn(
            `[Sonata] Desktop notification blocked — supports=${supports_html5_notification} ` +
                `setting=${api.settings.get('show_desktop_notifications')} ` +
                `permission=${supports_html5_notification ? Notification.permission : 'n/a'}`,
        );
    }
    return enabled;
}

/**
 * @typedef {Navigator & {clearAppBadge: Function, setAppBadge: Function} } navigator
 */

export function clearFavicon() {
    favicon?.badge(0);
    favicon = null;
    /** @type navigator */ (navigator)
        .clearAppBadge?.()
        .catch((e) => log.error('Could not clear unread count in app badge ' + e));
}

export function updateUnreadFavicon() {
    if (api.settings.get('show_tab_notifications')) {
        favicon = favicon ?? new converse.env.Favico({ type: 'circle', animation: 'pop' });
        const chats = _converse.state.chatboxes.models;
        // Closed chats stay in this collection (they're not destroyed, just
        // hidden from the chat lists) — a chat closed without ever being
        // read keeps a stale non-zero num_unread forever, which would
        // otherwise keep inflating the taskbar/dock badge even though no
        // visible chat list shows anything unread. Same for a chat with zero
        // stored messages: it cannot legitimately have anything unread, so a
        // non-zero counter there is provably stale (leftover from whatever
        // caused it, not something a filter could ever hide by accident).
        const contributors = [];
        const num_unread = chats.reduce((acc, chat) => {
            const n = chat.get('num_unread') || 0;
            if (chat.get('closed') || !chat.messages?.length) {
                if (n > 0) {
                    contributors.push(
                        `EXCLUDED jid=${chat.get('jid')} num_unread=${n} closed=${!!chat.get('closed')} messages=${chat.messages?.length ?? 'n/a'}`,
                    );
                }
                return acc;
            }
            if (n > 0) {
                contributors.push(`COUNTED  jid=${chat.get('jid')} num_unread=${n} messages=${chat.messages.length}`);
            }
            return acc + n;
        }, 0);
        console.log(`[Sonata] updateUnreadFavicon: total=${num_unread}`, contributors);
        favicon.badge(num_unread);
        /** @type navigator */ (navigator)
            .setAppBadge?.(num_unread)
            .catch((e) => log.error('Could set unread count in app badge - ' + e));
    }
}

/**
 * @param {Array<Object>} references - A list of objects representing XEP-0372 references
 * @param {string} muc_jid
 * @param {string} nick
 */
function isReferenced(references, muc_jid, nick) {
    const bare_jid = _converse.session.get('bare_jid');
    const check = (r) => [bare_jid, `${muc_jid}/${nick}`].includes(r.uri.replace(/^xmpp:/, ''));
    return references.reduce((acc, r) => acc || (r.uri && check(r)), false);
}

/**
 * Is this a group message for which we should notify the user?
 * @param {MUCMessageAttributes} attrs
 */
export async function shouldNotifyOfGroupMessage(attrs) {
    if (!attrs?.body && !attrs?.message) {
        // attrs.message is used by 'info' messages
        return false;
    }
    const jid = attrs.from;
    const muc_jid = attrs.from_muc;
    const notify_all = api.settings.get('notify_all_room_messages');
    const room = _converse.state.chatboxes.get(muc_jid);
    const resource = Strophe.getResourceFromJid(jid);
    const sender = (resource && Strophe.unescapeNode(resource)) || '';
    let is_mentioned = false;
    const nick = room.get('nick');

    if (api.settings.get('notify_nicknames_without_references')) {
        is_mentioned = new RegExp(`\\b${nick}\\b`).test(attrs.body);
    }

    const is_not_mine = sender !== nick;
    const should_notify_user =
        notify_all === true ||
        (Array.isArray(notify_all) && notify_all.includes(muc_jid)) ||
        isReferenced(attrs.references, muc_jid, nick) ||
        is_mentioned;

    if (is_not_mine && !!should_notify_user) {
        /**
         * *Hook* which allows plugins to run further logic to determine
         * whether a notification should be sent out for this message.
         * @event _converse#shouldNotifyOfGroupMessage
         * @example
         *  api.listen.on('shouldNotifyOfGroupMessage', (should_notify) => {
         *      return should_notify && flurb === floob;
         *  });
         */
        const should_notify = await api.hook('shouldNotifyOfGroupMessage', attrs, true);
        return should_notify;
    }
    return false;
}

async function shouldNotifyOfInfoMessage(attrs) {
    if (!attrs.from_muc) {
        return false;
    }
    const room = await api.rooms.get(attrs.from_muc);
    if (!room) {
        return false;
    }
    const nick = room.get('nick');
    const muc_jid = attrs.from_muc;
    const notify_all = api.settings.get('notify_all_room_messages');
    return (
        notify_all === true ||
        (Array.isArray(notify_all) && notify_all.includes(muc_jid)) ||
        isReferenced(attrs.references, muc_jid, nick)
    );
}

/**
 * @async
 * @method shouldNotifyOfMessage
 * @param {MessageData|MUCMessageData} data
 */
function shouldNotifyOfMessage(data) {
    const { attrs } = data;
    if (!attrs || attrs.is_forwarded) {
        return false;
    }
    // <message type="error"> is a delivery-failure report about something WE
    // sent, never a message from a person -- but it passes the "not empty"
    // test below (the failure stanza echoes the original payload). Together
    // with the marker echo loop (see sendMarker in headless/shared/actions.js)
    // it produced a notification storm for every bounce while the tab was in
    // the background.
    if (attrs['type'] === 'error') {
        return false;
    }
    // A live stanza that carries <delay> was REPLAYED by the server (stream
    // management resend after a reconnect or page reload, offline storage, MUC
    // history): its content is old and quite possibly already read. The
    // redelivery guard only remembers ids for the lifetime of the page, so
    // after a reload the server's resend of an hour-old message popped a
    // desktop notification and sound for it again. Never notify for those.
    if (attrs.is_delayed && attrs.time) {
        const age_ms = Date.now() - new Date(attrs.time).getTime();
        if (age_ms > 2 * 60 * 1000) {
            return false;
        }
    }
    // Checked once here, before branching by message type, so a muted chat
    // (1:1 or groupchat) never produces a desktop notification or sound,
    // regardless of mentions/references/other reasons that would otherwise
    // trigger one.
    const chatbox_jid = attrs.from_muc || (attrs.from && Strophe.getBareJidFromJid(attrs.from));
    if (chatbox_jid && _converse.state.chatboxes.get(chatbox_jid)?.get('muted')) {
        return false;
    }
    if (attrs['type'] === 'groupchat') {
        return shouldNotifyOfGroupMessage(attrs);
    } else if (attrs['type'] === 'info') {
        return shouldNotifyOfInfoMessage(attrs);
    } else if (attrs.is_headline) {
        // We want to show notifications for headline messages.
        return isMessageToHiddenChat(attrs.from);
    }

    const bare_jid = _converse.session.get('bare_jid');
    const is_me = Strophe.getBareJidFromJid(attrs.from) === bare_jid;
    return (
        !isEmptyMessage(attrs) &&
        !is_me &&
        (api.settings.get('show_desktop_notifications') === 'all' || isMessageToHiddenChat(attrs.from))
    );
}

export function showFeedbackNotification(data) {
    if (data.klass === 'error' || data.klass === 'warn') {
        const n = new Notification(data.subject, {
            body: data.message,
            lang: i18n.getLocale(),
            icon: api.settings.get('notification_icon'),
        });
        setTimeout(n.close.bind(n), 5000);
    }
}

/**
 * Creates an HTML5 Notification to inform of a change in a
 * contact's chat state.
 * @param {RosterContact} contact
 */
function showChatStateNotification(contact) {
    if (api.settings.get('chatstate_notification_blacklist')?.includes(contact.get('jid'))) {
        // Don't notify if the user is being ignored.
        return;
    }
    const chat_state = contact.presence.get('show');
    let message = null;
    if (chat_state === 'offline') {
        message = __('has gone offline');
    } else if (chat_state === 'away') {
        message = __('has gone away');
    } else if (chat_state === 'dnd') {
        message = __('is busy');
    } else if (chat_state === 'online') {
        message = __('has come online');
    }
    if (message === null) {
        return;
    }
    const n = new Notification(contact.getDisplayName(), {
        body: message,
        lang: i18n.getLocale(),
        icon: api.settings.get('notification_icon'),
    });
    setTimeout(() => n.close(), 5000);
}

/**
 * Shows an HTML5 Notification with the passed in message
 * @param {MessageData|MUCMessageData} data
 */
function showMessageNotification(data) {
    const { attrs } = data;
    if (attrs.is_error) {
        return;
    }

    if (!areDesktopNotificationsEnabled()) {
        return;
    }
    let title, roster_item;
    const full_from_jid = attrs.from;
    const from_jid = Strophe.getBareJidFromJid(full_from_jid);
    if (attrs.type == 'info') {
        title = attrs.message;
    } else if (attrs.type === 'headline') {
        if (!from_jid.includes('@') || api.settings.get('allow_non_roster_messaging')) {
            title = __('Notification from %1$s', from_jid);
        } else {
            return;
        }
    } else if (!from_jid.includes('@')) {
        // workaround for Prosody which doesn't give type "headline"
        title = __('Notification from %1$s', from_jid);
    } else if (attrs.type === 'groupchat') {
        title = __('%1$s says', Strophe.getResourceFromJid(full_from_jid));
    } else {
        if (_converse.state.roster === undefined) {
            log.error('Could not send notification, because roster is undefined');
            return;
        }
        roster_item = _converse.state.roster.get(from_jid);
        if (roster_item !== undefined) {
            title = __('%1$s says', roster_item.getDisplayName());
        } else {
            if (api.settings.get('allow_non_roster_messaging')) {
                title = __('%1$s says', from_jid);
            } else {
                return;
            }
        }
    }

    let body;
    if (attrs.type == 'info') {
        body = attrs.reason;
    } else {
        body = attrs.is_encrypted ? attrs.plaintext : attrs.body;
        if (!body) {
            return;
        }
    }

    console.log(`[Sonata] Showing desktop notification: "${title}"`);
    const n = new Notification(title, {
        body,
        lang: i18n.getLocale(),
        icon: api.settings.get('notification_icon'),
        requireInteraction: !api.settings.get('notification_delay'),
        // One popup per chat: a newer message from the same chat replaces the
        // older one (and `renotify` still alerts), instead of stacking up.
        tag: `sonata-msg-${from_jid}`,
        renotify: true,
    });
    n.onerror = (ev) => console.error('[Sonata] Notification error event:', ev);
    n.onshow = () => console.log('[Sonata] Notification onshow fired');
    if (api.settings.get('notification_delay')) {
        setTimeout(() => n.close(), api.settings.get('notification_delay'));
    }
    n.onclick = function (event) {
        event.preventDefault();
        window.focus();
        // In the Electron shell, the app is close-to-tray: the window is
        // hidden rather than closed, so window.focus() has nothing to focus.
        // Ask the main process to un-hide + focus it instead (no-op on web).
        window.electronApp?.focusWindow();
        const chat = _converse.state.chatboxes.get(from_jid);
        chat.maybeShow(true);
    };
}

/**
 * Shows an HTML5 Notification for an incoming XEP-0353 (Jingle Message
 * Initiation) call invite.
 *
 * The call-signaling stanza's human-readable text sits inside a
 * `p1:push:customize` payload (a mobile-push convention), not as a direct
 * `<body>` child of the `<message>` -- the same nested-body shape
 * `parseMessage()` deliberately no longer surfaces as `attrs.body`, since
 * unscoping that lookup was what let an OMEMO push-placeholder leak into
 * chat bubbles (see chat/parsers.js's directBody comment). That's correct
 * for regular messages, but it also makes every incoming-call stanza look
 * like an empty message to `shouldNotifyOfMessage()`, which silently
 * declines to notify for it -- the ringing modal (driven by the raw
 * stanza, not `attrs.body`) still shows fine, so this went unnoticed
 * until someone reported the desktop notification specifically missing
 * for calls while it worked for regular messages. Rather than teach the
 * generic message-notification pipeline about JMI's payload shape, the
 * call feature triggers its own 'incomingCall' event with what it already
 * knows (the caller's display name and JID), bypassing `attrs.body`
 * entirely.
 * @param {{ callerName: string, jid: string }} data
 */
// Tracks the Notification currently shown for a ringing call so it can be
// dismissed from closeIncomingCallNotification() below -- see there for why
// this is needed (requireInteraction:true means it never auto-dismisses).
let activeCallNotification = null;

export function handleIncomingCallNotification(data) {
    if (!areDesktopNotificationsEnabled()) {
        return;
    }
    // Defensive: close any notification still open from a prior call before
    // showing a new one. The `tag` below is meant to make the browser do
    // this coalescing on its own, but it wasn't reliably happening in
    // practice (observed multiple stacked "Incoming call" toasts for
    // sequential calls) -- belt-and-suspenders against any caller path that
    // ends up skipping closeIncomingCallNotification().
    activeCallNotification?.close();
    const { callerName, jid } = data;
    const n = new Notification(__('Incoming call'), {
        body: __('%1$s is calling you', callerName),
        lang: i18n.getLocale(),
        icon: api.settings.get('notification_icon'),
        requireInteraction: true,
        tag: 'sonata-incoming-call',
    });
    activeCallNotification = n;
    n.onclick = function (event) {
        event.preventDefault();
        window.focus();
        window.electronApp?.focusWindow();
        const chat = _converse.state.chatboxes.get(jid);
        chat?.maybeShow(true);
    };
}

/**
 * Dismisses the native "Incoming call" notification, if one is showing.
 * `requireInteraction: true` (needed so the call notification doesn't
 * disappear on its own mid-ring) also means nothing closes it automatically
 * -- without this, a call handled via the in-app modal (accepted, rejected,
 * retracted by the caller, or answered on another device) left the OS-level
 * notification stuck, and repeated calls stacked multiple phantom "Incoming
 * call" toasts on top of each other. Wired to heading.js's 'incomingCallEnded'
 * trigger, which fires from the single chokepoint where the in-app ringing
 * modal closes for any reason.
 */
export function closeIncomingCallNotification() {
    activeCallNotification?.close();
    activeCallNotification = null;
}

function playSoundNotification() {
    if (api.settings.get('play_sounds') && window.Audio !== undefined) {
        const audioOgg = new Audio(api.settings.get('sounds_path') + 'msg_received.ogg');
        const canPlayOgg = audioOgg.canPlayType('audio/ogg');
        if (canPlayOgg === 'probably') {
            return audioOgg.play();
        }
        const audioMp3 = new Audio(api.settings.get('sounds_path') + 'msg_received.mp3');
        const canPlayMp3 = audioMp3.canPlayType('audio/mp3');
        if (canPlayMp3 === 'probably') {
            audioMp3.play();
        } else if (canPlayOgg === 'maybe') {
            audioOgg.play();
        } else if (canPlayMp3 === 'maybe') {
            audioMp3.play();
        }
    }
}

/**
 * Event handler for the on('message') event. Will call methods
 * to play sounds and show HTML5 notifications.
 */
// Flood guard. Notifications used to have NO limit: every stanza that passed
// shouldNotifyOfMessage() became its own popup and its own sound. Whatever the
// source -- a loop between two clients, a backlog flushed on reconnect, a
// replay -- that turned into thousands of notifications (4,700 logged events
// for two messages in one incident). This caps the damage regardless of cause:
//  - never notify twice for the same message id;
//  - at most NOTIFY_MAX_PER_WINDOW notifications (and sounds) per window;
//  - popups from one chat share a `tag`, so they replace each other instead of
//    stacking (see showMessageNotification).
const NOTIFY_WINDOW_MS = 10000;
const NOTIFY_MAX_PER_WINDOW = 5;
const NOTIFIED_IDS_MAX = 300;
/** @type {number[]} */
const recent_notification_times = [];
/** @type {Set<string>} */
const notified_message_ids = new Set();
let flood_warned_at = 0;

/**
 * @param {{ msgid?: string }} attrs
 * @returns {boolean} whether a notification may be shown for this message
 */
function allowNotification(attrs) {
    const now = Date.now();
    const id = attrs?.msgid;
    if (id && notified_message_ids.has(id)) {
        return false;
    }
    while (recent_notification_times.length && now - recent_notification_times[0] > NOTIFY_WINDOW_MS) {
        recent_notification_times.shift();
    }
    if (recent_notification_times.length >= NOTIFY_MAX_PER_WINDOW) {
        if (now - flood_warned_at > NOTIFY_WINDOW_MS) {
            flood_warned_at = now;
            console.warn(
                `[Sonata] Notification flood guard: suppressing notifications ` +
                    `(more than ${NOTIFY_MAX_PER_WINDOW} in ${NOTIFY_WINDOW_MS / 1000}s)`,
            );
        }
        return false;
    }
    recent_notification_times.push(now);
    if (id) {
        notified_message_ids.add(id);
        if (notified_message_ids.size > NOTIFIED_IDS_MAX) {
            notified_message_ids.delete(notified_message_ids.values().next().value);
        }
    }
    return true;
}

export async function handleMessageNotification(data) {
    const should_notify = await shouldNotifyOfMessage(data);
    console.log(
        `[Sonata] handleMessageNotification: from=${data?.attrs?.from} type=${data?.attrs?.type} ` +
            `id=${data?.attrs?.msgid} delayed=${data?.attrs?.is_delayed} time=${data?.attrs?.time} ` +
            `document.hidden=${document.hidden} electron_window_hidden=${is_electron_window_hidden} should_notify=${should_notify}`,
    );
    if (!should_notify) {
        return false;
    }
    if (!allowNotification(data.attrs)) {
        return false;
    }
    /**
     * Triggered when a notification (sound or HTML5 notification) for a new
     * message has will be made.
     * @event _converse#messageNotification
     * @type {MessageData|MUCMessageData}
     * @example _converse.api.listen.on('messageNotification', data => { ... });
     */
    api.trigger('messageNotification', data);
    try {
        playSoundNotification();
    } catch (error) {
        // Likely "play() failed because the user didn't interact with the document first"
        log.error(error);
    }
    showMessageNotification(data);
}

export function handleFeedback(data) {
    if (areDesktopNotificationsEnabled()) {
        showFeedbackNotification(data);
    }
}

/**
 * Event handler for on('contactPresenceChanged').
 * Will show an HTML5 notification to indicate that the chat status has changed.
 * @param {RosterContact} contact
 */
export function handleChatStateNotification(contact) {
    if (areDesktopNotificationsEnabled() && api.settings.get('show_chat_state_notifications')) {
        showChatStateNotification(contact);
    }
}

/**
 * @param {RosterContact} contact
 */
function showContactRequestNotification(contact) {
    const n = new Notification(contact.getDisplayName(), {
        body: __('wants to be your contact'),
        lang: i18n.getLocale(),
        icon: api.settings.get('notification_icon'),
    });
    setTimeout(() => n.close(), 5000);
}

/**
 * @param {RosterContact} contact
 */
export function handleContactRequestNotification(contact) {
    if (areDesktopNotificationsEnabled()) {
        showContactRequestNotification(contact);
    }
}

export function requestPermission() {
    if (!supports_html5_notification) {
        console.warn('[Sonata] window.Notification is not available in this environment');
        return;
    }
    console.log(`[Sonata] Notification.permission is currently "${Notification.permission}"`);
    if (!['denied', 'granted'].includes(Notification.permission)) {
        // Ask user to enable HTML5 notifications
        Notification.requestPermission().then((permission) =>
            console.log(`[Sonata] Notification.requestPermission() resolved: "${permission}"`),
        );
    }
}
